import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * interfaces
 */
import { IJsonValidatorOptions, JsonShape } from './interfaces';

/**
 * models
 */
import { AppValidationErrorsModel } from '../models';

/**
 * Validator class that provides JSON-awareness validation for form controls.
 *
 * All methods are static — no instantiation needed.
 *
 * Three complementary validators are exposed:
 * - {@link JsonValidator.isJson}              — value **must** be parseable as JSON
 * - {@link JsonValidator.isNotJson}           — value **must not** be parseable as JSON (any JSON value)
 * - {@link JsonValidator.isNotStructuredJson} — plain text; rejects JSON objects/arrays only
 *
 * Both validators treat an empty / null / undefined value as valid (no error) so
 * they compose cleanly with `AppValidators.required`.
 *
 * @abstract
 * @example
 * ```typescript
 * // Textarea that must contain any valid JSON
 * new FormControl('', [AppValidators.required, JsonValidator.isJson()])
 *
 * // Textarea that must contain a JSON object (not an array or primitive)
 * new FormControl('', [JsonValidator.isJson({ shape: 'object' })])
 *
 * // Textarea that must contain a JSON array
 * new FormControl('', [JsonValidator.isJson({ shape: 'array' })])
 *
 * // Input that must be plain text — not JSON
 * new FormControl('', [JsonValidator.isNotJson])
 * ```
 */
export abstract class JsonValidator {

    // ─── private helpers ────────────────────────────────────────────────────

    /**
     * Attempts to parse a string as JSON.
     *
     * @param value - The string to parse
     * @returns The parsed value on success, or `null` when parsing fails
     */
    public static tryParse(value: string): unknown {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }

    /**
     * Checks whether a successfully-parsed value satisfies the requested {@link JsonShape}.
     *
     * @param parsed  - The value returned by `JSON.parse`
     * @param shape   - The expected top-level shape
     * @returns `true` when the shape matches, `false` otherwise
     */
    private static _matchesShape(parsed: unknown, shape: JsonShape): boolean {
        switch (shape) {
            case 'object':
                return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
            case 'array':
                return Array.isArray(parsed);
            default:
                return true;
        }
    }

    // ─── public validators ───────────────────────────────────────────────────

    /**
     * Creates a validator function that ensures the control's value is valid JSON.
     *
     * An optional `shape` constraint can be provided to further restrict which
     * top-level JSON type is accepted (`'object'`, `'array'`, or the default `'any'`).
     *
     * Empty, `null`, and `undefined` values are considered valid so this validator
     * can be combined freely with `AppValidators.required`.
     *
     * @static
     * @param options - Optional shape constraint; defaults to `{ shape: 'any' }`
     * @returns A `ValidatorFn` that returns `ValidationErrors | null`
     *
     * @example
     * ```typescript
     * // Any valid JSON
     * new FormControl('', [JsonValidator.isJson()])
     *
     * // Must be a JSON object
     * new FormControl('', [JsonValidator.isJson({ shape: 'object' })])
     *
     * // Must be a JSON array
     * new FormControl('', [JsonValidator.isJson({ shape: 'array' })])
     * ```
     */
    public static isJson(options: IJsonValidatorOptions = {}): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            // ES6 destructuring
            const { value } = control;

            // void validation — empty / falsy values are not this validator's concern
            if (!value || typeof value !== 'string' || value.trim().length === 0) return null;

            const parsed = JsonValidator.tryParse(value.trim());

            // value is not valid JSON at all
            if (parsed === null) {
                return new AppValidationErrorsModel('isJson', true, { key: 'FORM.VALIDATION.INVALID_JSON' });
            }

            const { shape = 'any' } = options;

            // value is valid JSON but does not match the requested shape
            if (!JsonValidator._matchesShape(parsed, shape)) {

                if (shape === 'object') {
                    return new AppValidationErrorsModel('isJsonObject', true, { key: 'FORM.VALIDATION.JSON_MUST_BE_OBJECT' });
                }

                if (shape === 'array') {
                    return new AppValidationErrorsModel('isJsonArray', true, { key: 'FORM.VALIDATION.JSON_MUST_BE_ARRAY' });
                }

            }

            return null;
        };
    }

    /**
     * Validates that the control's value is **plain text** — i.e. not parseable as JSON.
     *
     * Use this when a field should accept free-form text and must never be a JSON value
     * (e.g. a notes field that must not accidentally receive a serialised object).
     *
     * Empty, `null`, and `undefined` values are considered valid so this validator
     * can be combined freely with `AppValidators.required`.
     *
     * @static
     * @param control - The form control to validate
     * @returns `ValidationErrors` with i18n configuration when the value is valid JSON,
     *          or `null` when the value is plain text (or empty)
     *
     * @example
     * ```typescript
     * // Plain-text only field
     * new FormControl('', [AppValidators.required, JsonValidator.isNotJson])
     *
     * // Test cases:
     * control.setValue('hello world');   // → valid (null)
     * control.setValue('{"key":"val"}'); // → invalid (ValidationErrors)
     * control.setValue('[1,2,3]');       // → invalid (ValidationErrors)
     * control.setValue('');             // → valid (null) — use required for non-empty
     * ```
     */
    public static isNotJson(control: AbstractControl): ValidationErrors | null {

        // ES6 destructuring
        const { value } = control;

        // void validation — empty / falsy values are not this validator's concern
        if (!value || typeof value !== 'string' || value.trim().length === 0) return null;

        const parsed = JsonValidator.tryParse(value.trim());

        // value successfully parsed as JSON — this is not allowed
        if (parsed !== null) {
            return new AppValidationErrorsModel('isNotJson', true, { key: 'FORM.VALIDATION.MUST_BE_PLAIN_TEXT' });
        }

        return null;
    }

    /**
     * Validates that the control's value is **plain text** for param fields that are sent as raw strings.
     *
     * Unlike {@link JsonValidator.isNotJson}, JSON primitives such as numeric strings (`"42"`) and
     * booleans (`"true"`) are allowed. Only structured JSON — objects and arrays — is rejected so
     * users cannot accidentally paste a serialised payload when plain-text mode is active.
     *
     * Empty, `null`, and `undefined` values are considered valid so this validator
     * can be combined freely with `AppValidators.required`.
     *
     * @static
     * @param control - The form control to validate
     * @returns `ValidationErrors` when the value parses as a JSON object or array,
     *          or `null` otherwise
     *
     * @example
     * ```typescript
     * new FormControl('', [JsonValidator.isNotStructuredJson])
     *
     * // Test cases:
     * control.setValue('hello world');   // → valid (null)
     * control.setValue('42');           // → valid (null)
     * control.setValue('{"key":"val"}'); // → invalid (ValidationErrors)
     * control.setValue('[1,2,3]');       // → invalid (ValidationErrors)
     * ```
     */
    public static isNotStructuredJson(control: AbstractControl): ValidationErrors | null {

        const { value } = control;

        if (!value || typeof value !== 'string' || value.trim().length === 0) return null;

        const parsed = JsonValidator.tryParse(value.trim());

        if (parsed !== null && typeof parsed === 'object') {
            return new AppValidationErrorsModel('isNotStructuredJson', true, { key: 'FORM.VALIDATION.MUST_BE_PLAIN_TEXT' });
        }

        return null;
    }

}
